import { Alert, ExpandableSection, Toggle } from '@amzn/awsui-components-react-v3';
import {
  Table,
  TableContentSelector,
  TableFiltering,
  TablePageSizeSelector,
  TablePagination,
  TablePreferences,
} from 'AWS-UI-Components-React';
import React from 'react';
import { CHANNELS } from '../../constants/channels';
import { REPORT_COLUMN_KEYS } from '../../constants/report_columns';
import { REPORT_TYPE } from '../../constants/report_type';
import { COMPONENT_TO_LINK } from '../../constants/urls';
import { deepClone, downloadFile, extractErrorMessage, marketplacesById } from '../../utilities';
import { deriveComponentKey, extractSubComponentName, getDisplayName } from '../../utils/components';
import logger from '../../utils/logging';
import metricsPublisher from '../../utils/metrics';
import { BaselineColumnButton } from '../buttons/baseline_column_button.jsx';
import { getJSClient } from '../js_client';
import { AccuracyDriversTable } from './accuracy_drivers_table';
import ComponentLink from './controls/component_link';
import DownloadDataWindow from './download_data_window';
import ReportSaveWindow from './report_save_window';
import ButtonWithTreeLines from './table/ButtonWithTreeLines';
import Entry from './table/Entry';
import TreeLine, { TreeLineModes } from './table/TreeLine';

const dataStarAttributeBorder = 'data-border';
const dataStarAttributeTopBorderDark = 'top-dark';
const dataStarAttributeBottomBorderDark = 'bottom-dark';
const dataStarAttributeBorderNormal = 'normal';

const EXPAND = 'expand';
const COLLAPSE = 'collapse';

const KEY_SEPARATOR = '@';

const channels = Object.values(CHANNELS);

export class AccuracyTable extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      loadError: false,
      initialLoadDone: false,
      loadDone: false,
      columnsMap: {},
      displayInMillions: true,
      saveInProgress: false,
      saveSucceeded: false,
      saveFailed: false,
      downloadInProgress: false,
      downloadSucceeded: false,
      downloadFailed: false,
      columnSetsLoaded: false,
      columnDefinitions: [],
      contentSelectorOptions: [],
      rows: [],
      queryExecutionId: null,
      queryDataScanned: null,
      selectedColumnSets: [],
      columnSetOverrides: {},
      expandedMap: {},
      oppuHierarchy: {},
      summaryTableColumnDefinitions: [],
      summaryTableRows: [],
      tableKey: 1,
      root: {},
      liveSnapshotId: this.props.appConfig.liveSnapshotId,
      subComponentRows: [],
      globalAccuracyMap: {},
      subComponentLoading: {},
      currentPayload: this.props.payload,
      numberOfCall: 2,
      errorState: 'Unknown Error',
      missingDataMessageVisible: true,
      payloadsThresholdCrossed: false,
      payloadsThresholdCrossedColumns: {},
      payloadsThresholdCrossedMessageVisible: true,
      marketplaceIdToLabel: {}
    };
    this.summaryMaps = [];
    this.headings = [];
    this.metadataIndices = [];
    this.accuracyList = [];
    this.loadedAccuracyData = [];
    this.indexToCollate = 0;
    this.completedQueries = 0;
    this.errorMessage = '';
    this.plAllChannelMapList = [];
    this.plAllChannelKeys = { sortedKeys: [], sortedHeadings: [] };
    this.plAllChannelFlag =
      this.props.payload.report.toUpperCase() === REPORT_TYPE.PL &&
      this.props.payload.channel.toUpperCase() === CHANNELS.ALL;
    this.reportPreferences = {
      componentReport: { columns: [], columnSets: [] },
      plReport: { columns: [], columnSets: [] },
    };
    this.savePreferencesPayload = {};
    this.nonAggregatedPayloads = this.props.nonAggregatedPayloads;
    this.nonAggregatedFlags = this.props.nonAggregatedFlags;
    this.nonAggregatedHeadings = [];
    this.initialHeadings = [];
    this.missingDataIndex = {};
    this.subComponentRows = [];
    this.subComponentRowsLoaded = 0;
    this.accuracyDriversPayload = {};
    this.accuracyDriversDisabled = true;
  }

  prepareMapping = () => {
    const allMapping = {};
    allMapping.name = 'root';
    const mappingList = [];
    for (const map of this.props.hierarchy) {
      map.mapping.channel = map.id.split('_')[1];
      if (map.id.split('_')[1] === this.props.payload.channel && this.props.payload.channel !== CHANNELS.ALL) {
        const channelList = [];
        channelList.push({ name: 'GMS', channel: map.id.split('_')[1] });
        channelList.push({ name: 'Units', channel: map.id.split('_')[1] });
        channelList.push({ name: 'Product GMS', channel: map.id.split('_')[1] });
        channelList.push(...map.mapping.mapping);
        allMapping.mapping = channelList;
        this.setState({ oppuHierarchy: allMapping });
        return;
      }
      mappingList.push(map.mapping);
    }
    allMapping.mapping = mappingList;
    this.setState({ oppuHierarchy: allMapping });
  };

  prepareColumnsMapping = () => {
    const columnsMapping = {};
    for (const column of this.props.columnsJson.columns) {
      columnsMapping[column.id] = column;
    }
    this.setState({ columnsMap: columnsMapping });
  };

  getFormat = (key) => {
    let column = key.split(KEY_SEPARATOR)[0];
    return this.state.columnsMap[column] ? this.state.columnsMap[column]['format'] : '';
  };

  initializeAccuracyDriversPayload = () => {
    this.accuracyDriversPayload = this.props.payload;
    this.accuracyDriversDisabled = this.props.accuracyDriversDisabled;
  };

  getHeadings = () => {
    let headings = [];
    let finalKey = [];
    for (let m = 0; m < this.nonAggregatedPayloads.length; m++) {
      const timeFrame = this.getTimeFrame(m);
      if (this.nonAggregatedPayloads[m].region !== '') {
        finalKey[m] = timeFrame;
      }
      if (this.nonAggregatedPayloads[m].marketplace !== '') {
        if (this.nonAggregatedFlags['aggregatedMarketplaceSelected']) {
          finalKey[m] = timeFrame;
        } else if (this.nonAggregatedFlags['aggregatedTimeFrameSelected']) {
          finalKey[m] = this.state.marketplaceIdToLabel[this.nonAggregatedPayloads[m].marketplace];
        } else {
          finalKey[m] = this.state.marketplaceIdToLabel[this.nonAggregatedPayloads[m].marketplace] + '_' + timeFrame;
        }
      }
      finalKey[m] = this.nonAggregatedPayloads[m].rollup === 'worldwide' ? timeFrame : finalKey[m];
      headings.push(finalKey[m]);
    }
    if (headings.length > 5) {
      let columnsMap= {};
      for (let column of this.props.columnsJson.columnsForPayloadsThresholdCrossed.columns) {
        columnsMap[column] = column;
      }
      this.setState({ payloadsThresholdCrossed: true, payloadsThresholdCrossedColumns: columnsMap });
    }
    return headings;
  };

  getTimeFrame = (index) => {
    let timeFrame = '';
    if (
      !this.nonAggregatedPayloads[index].timeFrame.includes('Q') &&
      this.nonAggregatedPayloads[index].timeFrame.includes('/')
    ) {
      this.nonAggregatedPayloads[index].timeFrame = this.nonAggregatedPayloads[index].timeFrame.replace(/-/g, '/');
      const currentDate = new Date(
        this.nonAggregatedPayloads[index].timeFrame.split('/')[0],
        this.nonAggregatedPayloads[index].timeFrame.split('/')[1] - 1
      );
      let year = currentDate.getFullYear();
      timeFrame = currentDate.toLocaleString('default', { month: 'short' }) + ' ' + year;
      return timeFrame;
    } else {
      return this.nonAggregatedPayloads[index].timeFrame;
    }
  };

  componentWillMount = () => {
    this.client = getJSClient();
    this.initialClientTasks();
  };

  componentDidUpdate = (prevProps) => {
    if (prevProps.downloadData !== this.props.downloadData && this.props.downloadData) {
      this.beginDownload();
    }
    if (prevProps.saveReport !== this.props.saveReport && this.props.saveReport) {
      this.beginSave();
    }
    if (prevProps.savedPreferences !== this.props.savedPreferences) {
      this.processReportPreferences();
    }
  };

  initialClientTasks = () => {
    this.prepareMapping();
    this.prepareColumnsMapping();
    this.initializeAccuracyDriversPayload();
    this.initializeMarketplaceIdToLabel();
    this.setState({ loadStartTimestamp: performance.now() });
    if (this.nonAggregatedPayloads.length) {
      for (let i = 0; i < this.nonAggregatedPayloads.length; i++) {
        this.pullDataWithRetryAndTimeout(i, 2);
      }
    } else {
      for (let i = 0; i < this.props.snapshots.length; i++) {
        this.pullDataWithRetryAndTimeout(i, 2);
      }
    }
    if (
      this.props.payload.report === REPORT_TYPE.COMPONENT.toLowerCase() ||
      this.props.payload.report === REPORT_TYPE.PL.toLowerCase()
    ) {
      this.processReportPreferences();
    }
  };

  initializeHeadingsConditionally = () => {
    if (this.nonAggregatedPayloads.length) {
      this.nonAggregatedHeadings = this.getHeadings();
    }
  };

  initializeMarketplaceIdToLabel = () => {
    this.setState({marketplaceIdToLabel: marketplacesById(this.props.marketplaceItems)}, this.initializeHeadingsConditionally);
  };

  processReportPreferences = () => {
    this.savePreferencesPayload = { preferencesToSave: { reportPreferences: [], user: this.props.user } };
    if (!this.props.savedPreferences || !this.props.savedPreferences['reportPreferences']) {
      this.savePreferencesPayload['preferencesToSave']['reportPreferences'].push(
        { columns: this.props.columnsJson.default['columns'], columnSets: [], reportType: 'component' },
        {
          columns: this.props.columnsJson.plSets[0]['columns'],
          columnSets: [this.props.columnsJson.plSets[0]['id']],
          reportType: 'pl',
        }
      );
    } else {
      this.savePreferencesPayload['preferencesToSave']['reportPreferences'] = this.props.savedPreferences[
        'reportPreferences'
      ];
    }
    for (const pref of this.savePreferencesPayload['preferencesToSave']['reportPreferences']) {
      if (pref['reportType'] === REPORT_TYPE.COMPONENT.toLowerCase()) {
        this.reportPreferences['componentReport'] = { columns: pref.columns, columnSets: pref.columnSets };
      } else if (pref['reportType'] === REPORT_TYPE.PL.toLowerCase()) {
        this.reportPreferences['plReport'] = { columns: pref.columns, columnSets: pref.columnSets };
      }
    }
    this.initializeColumnSetOverrides();
  };

  initializeColumnSetOverrides = () => {
    let columns, sets, columnSets;
    if (this.props.payload.report === REPORT_TYPE.COMPONENT.toLowerCase()) {
      [columns, sets, columnSets] = [
        this.reportPreferences['componentReport']['columns'],
        this.props.columnsJson.componentSets,
        this.reportPreferences['componentReport']['columnSets'],
      ];
    } else if (this.props.payload.report === REPORT_TYPE.PL.toLowerCase()) {
      [columns, sets, columnSets] = [
        this.reportPreferences['plReport']['columns'],
        this.props.columnsJson.plSets,
        this.reportPreferences['plReport']['columnSets'],
      ];
    }
    const columnSetColumns = sets
      .filter((set) => columnSets.includes(set['id']))
      .map((set) => set['columns'])
      .reduce((a, b) => a.concat(b), []);
    const columnSetOverrides = this.getColumnSetOverrides(columns, columnSetColumns);
    this.setState({ columnSetOverrides });
  };

  getColumnSetOverrides = (columns, columnSetColumns) => {
    const overrides = columns
      .filter((col) => !columnSetColumns.includes(col))
      .concat(columnSetColumns.filter((col) => !columns.includes(col)));
    const ColumnSetOverrides = {};
    for (const override of overrides) {
      ColumnSetOverrides[override] = override;
    }
    return ColumnSetOverrides;
  };

  preparePreferencesPayload = (reportType) => {
    for (const pref of this.savePreferencesPayload['preferencesToSave']['reportPreferences']) {
      if (pref['reportType'] === reportType) {
        this.savePreferencesPayload['preferencesToSave']['reportPreferences'] = this.savePreferencesPayload[
          'preferencesToSave'
        ]['reportPreferences'].filter((pref) => pref['reportType'] !== reportType);
      }
    }
  };

  setPreferences = (columns, columnSets) => {
    const reportType = this.props.payload.report;
    this.preparePreferencesPayload(reportType);
    this.savePreferencesPayload['preferencesToSave']['reportPreferences'].push({ columnSets, columns, reportType });
    this.client.saveUserPreferences(
      this.onSuccessfulSavePreferences,
      this.onFailedSavePreferences,
      this.savePreferencesPayload,
      this.props.user
    );
  };

  onSuccessfulSavePreferences = () => {
    this.props.onSavePreferences(this.savePreferencesPayload['preferencesToSave']);
  };

  onFailedSavePreferences = () => {
    this.props.resetButtons();
  };

  pullDataWithRetryAndTimeout = (index, retry) => {
    if (retry > 0) {
      return this.pullData(index, retry)
                 .then(() => this.sleep(5000))
                 .then(() => {retry = this.checkDone(retry);})
                 .then(() => this.pullDataWithRetryAndTimeout(index, retry-1));
    }
  };

  checkDone = (retry) => {
    if (this.state.loadDone) {
      retry--;
    }
    return retry;
  };

  pullData = async (index, retry) => {
    const channelsList = this.plAllChannelFlag ? channels : ['unknown'];
    for (let channel of channelsList) {
      const payloadType = this.nonAggregatedPayloads.length ? this.nonAggregatedPayloads[index] : this.props.payload;
      const payload = channel === 'unknown' ? payloadType : { ...this.props.payload, channel };
      this.client.pullAccuracyData(
        this.onAccuracyPull,
        this.onAccuracyFailure,
        payload,
        this.state.queryExecutionId,
        this.props.snapshots,
        index,
        null,
        retry
      );
    }
  };

  zip = (a, b) => a.map((x, i) => [x, b[i]]);

  onAccuracyPull = async (response, snapshotIndex, payload, item, retry) => {
    if (!this.nonAggregatedPayloads.length) {
      console.log(
        'Received response from snapshot %s: %s,%s,%s ',
        this.props.snapshots[snapshotIndex].id,
        response.queryExecutionStatus,
        response.queryExecutionId,
        response.queryDataScanned
      );
    }
    this.setState({ queryDataScanned: response.queryDataScanned });
    if (response.queryExecutionStatus === 'INCOMPLETE') {
      this.setState({ queryExecutionId: response.queryExecutionId });
      await this.sleep(2000);
      if (!this.state.loadDone) {
        console.log('Resend request: ' + this.state.queryExecutionId);
        this.pullData(snapshotIndex, retry);
      }
    } else if (response.queryExecutionStatus === 'COMPLETE') {
      if (this.state.loadDone && !this.state.loadError) {
        // Skip process if the report is already displayed from last request.
        return;
      }
      if (!this.nonAggregatedPayloads.length) {
        console.log(
          'Accuracy Data Pulled from %s (%d of %d)',
          this.props.snapshots[snapshotIndex].id,
          ++this.completedQueries,
          this.props.snapshots.length
        );
      } else {
        console.log('Accuracy Data Pulled for Dataset ' + (snapshotIndex + 1));
      }
      if (!this.plAllChannelFlag) {
        this.loadedAccuracyData[snapshotIndex] = {
          sortedKeys: response.sortedKeys,
          sortedHeadings: response.sortedHeadings,
          accuracyList: response.accuracyList,
          cpSummaryMap: response.cpSummaryMap,
          cplfSummaryMap: response.cplfSummaryMap,
          opSummaryMap: response.opSummaryMap
        };
        if (!response.accuracyList.length) { this.missingDataIndex[snapshotIndex] = this.nonAggregatedHeadings[snapshotIndex]; }
        this.initialHeadings = response.sortedHeadings;
      } else {
        let channel = payload.channel.toUpperCase();
        this.prepareAllChannelMap(response.accuracyList, snapshotIndex, channel);
        if (channel === CHANNELS.ALL) {
          this.plAllChannelKeys = { sortedKeys: response.sortedKeys, sortedHeadings: response.sortedHeadings };
        }

        if (this.plAllChannelMapList[snapshotIndex].size === channels.length) {
          this.loadedAccuracyData[snapshotIndex] = {
            sortedKeys: this.plAllChannelKeys.sortedKeys,
            sortedHeadings: this.plAllChannelKeys.sortedHeadings,
            accuracyList: this.prepareAllChannelPLList(snapshotIndex),
          };
        }
      }
      await this.checkLoadedAccuracy();
    } else {
      console.log('report failed with ' + response.queryExecutionStatus + ': ' + response.reportStatus);
      logger.error('Accuracy Data pull exception: ' + response.queryExecutionStatus,
                   {error: response.reportStatus, payload: this.props.payload});
      this.setState({loadError: true, loadDone: true, errorState: response.queryExecutionStatus});
    }
  };

  checkLoadedAccuracy = async () => {
    if (this.nonAggregatedPayloads.length) {
      while (
        this.indexToCollate < this.nonAggregatedPayloads.length &&
        this.indexToCollate < this.loadedAccuracyData.length &&
        this.loadedAccuracyData[this.indexToCollate]
      ) {
        await this.collateData(this.loadedAccuracyData, this.indexToCollate);
        this.indexToCollate = this.indexToCollate + 1;

      }
      if (this.indexToCollate === this.nonAggregatedPayloads.length) {
        this.processAccuracyResults();
      }
    } else {
      while (
        this.indexToCollate < this.props.snapshots.length &&
        this.indexToCollate < this.loadedAccuracyData.length &&
        this.loadedAccuracyData[this.indexToCollate]
      ) {
        await this.collateData(this.loadedAccuracyData, this.indexToCollate);
        this.indexToCollate = this.indexToCollate + 1;
      }
      if (this.indexToCollate === this.props.snapshots.length) {
        this.processAccuracyResults();
      }
    }
  };

  collateData = (accuracyData, snapshotIndex) => {
    let response = accuracyData[snapshotIndex];

    this.collateKeys(response.sortedKeys, snapshotIndex);
    this.collateHeadings(response.sortedHeadings, snapshotIndex);
    this.collateAccuracyList(response.accuracyList, snapshotIndex);
    this.summaryMaps.push(response.summaryMap);
    return true;
  };

  plRowSerialize = (item) => {
    return item['component'] + item['type'] + item['productLineID'];
  };

  prepareAllChannelMap = (accuracyList, snapshotIndex, channel) => {

    if (!this.plAllChannelMapList[snapshotIndex]) {
      this.plAllChannelMapList[snapshotIndex] = new Map();
    }

    let channelMap = this.plAllChannelMapList[snapshotIndex];
    if (!channelMap.has(channel)) {
      channelMap.set(channel, new Map());
      let map = channelMap.get(channel);
      for (let row of accuracyList) {
        map.set(this.plRowSerialize(row), row);
      }
    }
  };

  prepareAllChannelPLList = (snapshotIndex) => {
    let accuracyPLList = [];
    let channelMap = this.plAllChannelMapList[snapshotIndex];
    let allChannelMap = channelMap.get('ALL');
    for (let key of allChannelMap.keys()) {
      accuracyPLList.push(allChannelMap.get(key));
      for (let channel of channels.slice(1)) {
        if (channelMap.get(channel).has(key)) {
          accuracyPLList.push(channelMap.get(channel).get(key));
        }
      }
    }
    return accuracyPLList;
  };

  processAccuracyResults = () => {

    const reportType = this.props.payload.report.toUpperCase();

    if (reportType === REPORT_TYPE.COMPONENT) {
      this.buildSummaryTables();
    }

    const columns = this.generateColumns();
    const { columnDefinitions, contentSelectorOptions } = columns;

    this.setState({
      columnDefinitions: columnDefinitions,
      contentSelectorOptions: contentSelectorOptions
    }, () => {
      this.updateTableRows(reportType);
    });

    this.setState({ loadEndTimestamp: performance.now() });
    const responseTime = this.state.loadEndTimestamp - this.state.loadStartTimestamp;
    metricsPublisher.forMethod('accuracyReportLoadTime').publishTimer(responseTime);
    logger.info(`Report response time`, {
      responseTime: responseTime,
      report: this.props.payload.report,
      payload: this.props.payload
    });

    /* loadDone may be set to false once a new report is kicked off, but initialLoadDone should *always* remain
     * true once the page's first query is complete. */
    this.setState({ loadDone: true, initialLoadDone: true });
    this.props.onLoadDone({ reportType });
  };

  updateTableRows = (reportType) => {

    // OPPU hierarchy currently only for component report
    if (reportType === REPORT_TYPE.COMPONENT) {
      let root = new Entry('root', null, KEY_SEPARATOR, this.props.snapshots, this.nonAggregatedHeadings);
      let accuracyMap = this.processAccuracyList(this.accuracyList);
      console.log('Found the following accuracy data:');
      console.log(accuracyMap);
      this.setState({ globalAccuracyMap: deepClone(accuracyMap) });
      if (this.accuracyList.length !== 0) {
        this.setupHierarchy(this.state.oppuHierarchy, accuracyMap, root);
      }
      let newRows = this.state.rows.slice(0);
      this.flattenHierarchy(root, newRows);

      this.setState({ root, rows: newRows });
    } else {
      let newRow = [];
      for (let map of this.accuracyList) {
        newRow.push(
          this.buildRow(map, null, {
            [REPORT_COLUMN_KEYS.COMPONENT]: (subComponent) => `${getDisplayName(subComponent)}`
          })
        );
      }

      this.setState({ rows: newRow });
    }
  };

  sameColumn = (a, b) => {
    return a.split(KEY_SEPARATOR)[0] === b.split(KEY_SEPARATOR)[0];
  };

  collateKeys = (newKeys, snapshotIndex) => {
    this.collationIndices = [];
    this.metadataIndices = [];
    if (snapshotIndex && newKeys.length) {
      let keys = deepClone(this.keys);
      this.keys = [];
      while (newKeys.length) {
        let key = newKeys.shift();
        while (keys.length && this.sameColumn(keys[0], key)) {
          this.keys.push(keys.shift());
        }
        if (this.state.columnsMap[key].isMetadata) {
          this.collationIndices.push(-1);
        } else {
          if (this.nonAggregatedPayloads.length) {
            key = key + KEY_SEPARATOR + this.nonAggregatedHeadings[snapshotIndex];
          } else if (this.props.snapshots.length > 1) {
            key = key + KEY_SEPARATOR + this.props.snapshots[snapshotIndex].id;
          }
          this.collationIndices.push(this.keys.length);
          this.keys.push(key);
        }
      }
    } else if (snapshotIndex === 0) {
      for (let i = 0; i < newKeys.length; i++) {
        if (this.nonAggregatedPayloads.length) {
          if (this.state.columnsMap[newKeys[i]].isMetadata) {
            this.metadataIndices.push(i);
          } else {
            newKeys[i] = newKeys[i] + KEY_SEPARATOR + this.nonAggregatedHeadings[snapshotIndex];
          }
        } else if (this.props.snapshots.length > 1) {
          if (this.state.columnsMap[newKeys[i]].isMetadata) {
            this.metadataIndices.push(i);
          } else {
            newKeys[i] = newKeys[i] + KEY_SEPARATOR + this.props.snapshots[snapshotIndex].id;
          }
        }
        this.collationIndices.push(i);
      }
      this.keys = newKeys;
    }
  };

  collateHeadings = (newHeadings, snapshotIndex) => {
    for (let i = 0; i < newHeadings.length; i++) {
      if (this.collationIndices[i] !== -1) {
        let heading = newHeadings[i];
        if (this.nonAggregatedPayloads.length && !this.metadataIndices.includes(this.collationIndices[i])) {
          heading = '[' + this.nonAggregatedHeadings[snapshotIndex] + ']' + heading;
        } else if (this.props.snapshots.length > 1 && !this.metadataIndices.includes(this.collationIndices[i])) {
          heading =
            (this.props.snapshots[snapshotIndex].id === this.state.liveSnapshotId
              ? '[live]'
              : '[' + this.props.snapshots[snapshotIndex].name + ']') + heading;
        }
        this.headings.splice(this.collationIndices[i], 0, heading);
      }
    }
  };

  collateAccuracyList = (newAccuracyList, snapshotIndex) => {
    if (snapshotIndex) {
      let newAccuracyMap = {};
      if (this.props.payload.report.toUpperCase() === REPORT_TYPE.COMPONENT) {
        newAccuracyList.forEach((map) => (newAccuracyMap[map.channel + map.component] = map));
        for (let i = 0; i < this.accuracyList.length; i++) {
          let row = this.accuracyList[i];
          let newRow = newAccuracyMap[row.channel + row.component];
          if (newRow) {
            this.accuracyList[i] = this.collateAccuracyRow(row, newRow, snapshotIndex);
          }
        }
      } else if (this.props.payload.report.toUpperCase() === REPORT_TYPE.PL) {
        newAccuracyList.forEach((map) => (newAccuracyMap[this.plRowSerialize(map) + map.channel] = map));
        for (let i = 0; i < this.accuracyList.length; i++) {
          let row = this.accuracyList[i];
          let newRow = newAccuracyMap[this.plRowSerialize(row) + row.channel];
          this.accuracyList[i] = this.collateAccuracyRow(row, newRow, snapshotIndex);
        }
      }
    } else {
      for (let newRow of newAccuracyList) {
        let row = {};
        this.accuracyList.push(this.collateAccuracyRow(row, newRow, snapshotIndex));
      }
    }
  };

  collateAccuracyRow = (row, newRow, snapshotIndex) => {
    for (let key of Object.keys(newRow)) {
      let newKey = key;
      if (this.nonAggregatedPayloads.length && !this.state.columnsMap[key].isMetadata) {
        newKey = key + KEY_SEPARATOR + this.nonAggregatedHeadings[snapshotIndex];
      } else if (this.props.snapshots.length > 1 && !this.state.columnsMap[key].isMetadata) {
        newKey = key + KEY_SEPARATOR + this.props.snapshots[snapshotIndex].id;
      }
      row[newKey] = newRow[key];
    }
    return row;
  };

  processAccuracyList = (accuracyList) => {
    const accuracyMap = {};
    accuracyList.forEach((map) => (accuracyMap[map.channel + map.component] = this.buildRow(map)));
    return accuracyMap;
  };

  setupHierarchy = (oppuHierarchy, accuracyMap, parent) => {

    const levelForComponentRows = this.getLevelForComponentRows();

    const current = this.createEntry(oppuHierarchy, parent, accuracyMap);
    parent.addChild(current);
    if (oppuHierarchy.mapping) {
      for (const child of oppuHierarchy.mapping) {
        this.setupHierarchy(child, accuracyMap, current);
        const temp = this.state.expandedMap;
        temp[current.id] = current.level <= levelForComponentRows;
        this.setState({ expandedMap: temp });
      }
    }
    if (this.needAggregation(parent)) {
      parent.aggregateAccuracy(current);
    }
  };

  updateHierarchy = (current, accuracyMap, componentsToUpdate) => {
    if (current === null) {
      return;
    }

    if (current.level > 0 && componentsToUpdate.has(current.component)) {
      current.channel = this.props.payload.channel !== CHANNELS.ALL ? this.props.payload.channel : current.channel;
      const itemKey = `${current.channel}${current.component}`;
      if (Object.keys(accuracyMap).includes(itemKey)) {
        let accuracyRow = {
          ...accuracyMap[itemKey],
          component: extractSubComponentName(accuracyMap[itemKey].component),
        };
        current.setAccuracy(accuracyRow);
      }
    }

    for (const child of current.getChildren()) {
      this.updateHierarchy(child, accuracyMap, componentsToUpdate);
    }
  };

  needAggregation = (parent) =>
    /* This function decide whether to aggregate children values to parent row.
     * default should be only excluding Contribution Profit rows. ie. parent.component !== 'Contribution Profit'
     * but we want to temporary disable the row aggregation on all channels for now to avoid validation confusion */
    parent.component === 'Total GMS' || parent.component === 'Total Units Served' || parent.component === 'Product GMS';

  createEntry = (hierarchyEntry, parent, accuracyMap) => {
    let current = new Entry(hierarchyEntry, parent, KEY_SEPARATOR, this.props.snapshots, this.nonAggregatedHeadings);
    let name = hierarchyEntry.name;
    current.channel = this.props.payload.channel !== CHANNELS.ALL ? this.props.payload.channel : current.channel;
    if (Object.keys(accuracyMap).includes(current.channel + name)) {
      current.setAccuracy(accuracyMap[current.channel + name]);
    } else if (Object.keys(accuracyMap).includes(name.toUpperCase() + current.channel)) {
      const accuracy = accuracyMap[name.toUpperCase() + current.channel];
      accuracy.component = name;
      current.setAccuracy(accuracy);
    } else {
      let accuracy = {};
      accuracy['component'] = name;
      accuracy['type'] = '-';
      accuracy['currencyCode'] = 'USD';
      accuracy['exchangeRate'] = '1.0';
      let defaultedColumns = [
        { name: 'percentageRevShare', value: 'N/A' },
        { name: 'percentageRevShareStandardDeviation', value: 'N/A' },
        { name: 'weight', value: '-' },
        { name: 'cognosWeight', value: '-' },
        { name: 'timestamp', value: '-' },
      ];
      if (this.nonAggregatedPayloads.length) {
        for (let slice = 0; slice < this.nonAggregatedPayloads.length; slice++) {
          for (let col of defaultedColumns) {
            accuracy[col.name + KEY_SEPARATOR + this.nonAggregatedHeadings[slice]] = col.value;
          }
        }
      } else if (this.props.snapshots.length > 1) {
        for (let snapshot of this.props.snapshots) {
          for (let col of defaultedColumns) {
            accuracy[col.name + KEY_SEPARATOR + snapshot.id] = col.value;
          }
        }
      } else {
        for (let col of defaultedColumns) {
          accuracy[col.name] = col.value;
        }
      }
      current.setAccuracy(accuracy);
    }
    return current;
  };

  sleep = (milliseconds) => new Promise((resolve) => setTimeout(resolve, milliseconds));

  onAccuracyFailure = (error, retry) => {
    // Ignore failure for first call
    if (retry !== this.state.numberOfCall) {
      console.log(error);
      logger.error('Load report failure', error, this.props.payload);
      this.errorMessage = extractErrorMessage(error);
      this.setState({loadError: true, loadDone: true});
    }
  };

  onExpandOrCollapse = (id) => {
    const temp = this.state.expandedMap;
    temp[id] = !temp[id];
    this.setState({ expandedMap: temp });

    const newRows = [];
    this.flattenHierarchy(this.state.root, newRows);

    this.setState({ rows: newRows });
  };

  flattenHierarchy = (cur, newRows, position) => {
    if (cur === null) {
      return;
    }
    if (cur.style === null || cur.style !== 'top') {
      if (cur.level >= 0) {
        const row = cur.getAccuracy();
        row.level = cur.getLevel();
        row.parent = cur.getParent();
        row.children = cur.getChildren();
        row.id = cur.getId();
        row.style = cur.getStyle();
        if (position === 'last') {
          row.isLast = true;
        } else if (position === 'first') {
          row.isFirst = true;
        }

        newRows.push(row);
        if (!this.state.expandedMap[cur.id]) return;
      }
      for (const [index, child] of cur.getChildren().entries()) {
        this.flattenHierarchy(child, newRows, index === cur.getChildren().length - 1 ? 'last' : '');
      }
    } else {
      for (const [index, child] of cur.getChildren().entries()) {
        if (cur.level >= 0 && !this.state.expandedMap[cur.id]) continue;
        this.flattenHierarchy(child, newRows, index === 0 ? 'first' : '');
      }
      if (cur.level >= 0) {
        const row = cur.getAccuracy();
        row.level = cur.getLevel();
        row.parent = cur.getParent();
        row.children = cur.getChildren();
        row.id = cur.getId();
        row.style = cur.getStyle();
        if (position === 'first') {
          row.isFirst = true;
        } else if (position === 'last') {
          row.isLast = true;
        }
        newRows.push(row);
      }
    }
  };

  beginDownload = () => {
    this.setState({
      downloadInProgress: true,
      downloadSucceeded: false,
      downloadFailed: false,
    });
  };

  onDownloadData = () => {
    if (this.nonAggregatedPayloads.length) {
      return <DownloadDataWindow onCancel={this.cancelDownload} onSubmit={this.downloadData} />;
    } else {
      this.downloadData();
      this.setState({ downloadInProgress: false, downloadSucceeded: true, downloadFailed: false });
    }
  };

  downloadData = (parameter) => {
    const array = [];

    console.log('Preparing accuracy report download...');

    if (parameter === 'rows') {
      logger.info('User download row-wise xls report', { report: this.nonAggregatedPayloads });
      this.initialHeadings.unshift('DataSets');
      array.push(this.initialHeadings.join('\t'));
      for (let m = 0; m < this.nonAggregatedHeadings.length; m++) {
        for (let map = 0; map < this.accuracyList.length; map++) {
          const componentDataRow = this.getDataRowFromKeys(this.accuracyList[map], m);

          const channel = this.accuracyList[map][REPORT_COLUMN_KEYS.CHANNEL];
          const component = this.accuracyList[map][REPORT_COLUMN_KEYS.COMPONENT];
          const subComponentData = this.getSubComponentRows(component, channel);
          const subComponentDataRows = subComponentData.map((data) =>
            this.getDataRowFromKeys(data, m, this.getSubComponentDisplayFormatFunctions(component)).join('\t')
          );

          array.push(componentDataRow.join('\t'));
          array.push(...subComponentDataRows);
        }
      }
    } else {
      logger.info('User download side-by-side xls report', { report: this.props.payload.report });
      array.push(this.headings.join('\t'));
      if (this.plAllChannelFlag) {
        let channelOrderMap = new Map();
        const accuracyList = deepClone(this.accuracyList);

        for (let item of accuracyList) {
          let channel = item['channel'].toUpperCase();
          if (!channelOrderMap.has(channel)) {
            channelOrderMap.set(channel, []);
          }
          channelOrderMap.get(channel).push(item);
        }

        this.accuracyList = [];
        for (let channel of channels) {
          if (channelOrderMap.has(channel)) {
            this.accuracyList = this.accuracyList.concat(...channelOrderMap.get(channel));
          }
        }
      }

      for (const accuracyRowItem of this.accuracyList) {
        const component = accuracyRowItem[REPORT_COLUMN_KEYS.COMPONENT];
        const channel = accuracyRowItem[REPORT_COLUMN_KEYS.CHANNEL];
        const subComponents = this.getSubComponentRows(component, channel);

        const componentDataRow = this.getDataRow(accuracyRowItem, {
          [REPORT_COLUMN_KEYS.COMPONENT]: (componentItem) => `${getDisplayName(componentItem)}`
        });
        const subComponentDataRows = subComponents
          .map((subComponent) => this.getDataRow(subComponent, this.getSubComponentDisplayFormatFunctions(component)))
          .map((row) => row.join('\t'));

        array.push(componentDataRow.join('\t'));
        array.push(...subComponentDataRows);
      }
    }

    const data = array.join('\n');

    downloadFile(data, 'Accuracy Data.xls');

    this.setState({ downloadInProgress: false, downloadSucceeded: true, downloadFailed: false });
    this.props.resetButtons();
  };

  getDataRowFromKeys = (dataObject, headingIndex, displayFormatFunctions) => {
    let rows = [];
    for (let key = 0; key < this.keys.length; key++) {
      const columnKey = this.keys[key];
      if (columnKey.endsWith(this.nonAggregatedHeadings[headingIndex]) || columnKey.includes(KEY_SEPARATOR) === false) {
        const formatFunction = displayFormatFunctions ? displayFormatFunctions[columnKey] : null;
        const value = formatFunction ? formatFunction(dataObject[columnKey]) : dataObject[columnKey];

        key === 0 ? rows.push(this.nonAggregatedHeadings[headingIndex]) && rows.push(value) : rows.push(value);
      }
    }

    return rows;
  };

  getDataRow = (item, displayFormatFunctions) => {
    return this.keys.map((key) => {
      const formatFunction = displayFormatFunctions ? displayFormatFunctions[key] : null;
      return formatFunction ? formatFunction(item[key]) : item[key];
    });
  };

  getSubComponentRows = (parentComponent, parentChannel) => {
    const { globalAccuracyMap } = this.state;

    let subComponents = [];
    const parentComponentKey = deriveComponentKey(parentComponent);
    for (let key of Object.keys(globalAccuracyMap)) {
      const value = globalAccuracyMap ? globalAccuracyMap[key] : {};
      const { parentComponent, channel } = value || {};

      if (parentComponent === parentComponentKey && channel === parentChannel) {
        subComponents.push(value);
      }
    }

    return subComponents;
  };

  getSubComponentDisplayFormatFunctions = (component) => {
    return {
      [REPORT_COLUMN_KEYS.COMPONENT]: (subComponentName) =>
        `  ${component}/${extractSubComponentName(subComponentName)}`,
    };
  };

  beginSave = () => {
    this.setState({
      saveInProgress: true,
      saveSucceeded: false,
      saveFailed: false,
    });
  };

  cancelSave = () => {
    this.setState({
      saveInProgress: false,
      saveSucceeded: false,
      saveFailed: false,
    });
    this.props.resetButtons();
  };

  cancelDownload = () => {
    this.setState({
      downloadInProgress: false,
      downloadSucceeded: false,
      downloadFailed: false,
    });
    this.props.resetButtons();
  };

  saveReport = (report) => {
    this.client.saveReport(this.onSuccessfulSave, this.onFailedSave, { reportToSave: report });
  };

  onSuccessfulSave = (reportName) => {
    this.setState({
      saveInProgress: false,
      saveSucceeded: true,
      saveFailed: false,
    });
    console.log('Save succeeded');
    this.props.onSaveReport(reportName);
    this.props.resetButtons();
  };

  onFailedSave = (payload) => {
    let message = '';
    if (payload.response.data.message) {
      message = `: ${payload.response.data.message}`;
    }
    console.log(`Save failed${message}`);
    this.setState({
      saveInProgress: false,
      saveSucceeded: false,
      saveFailed: true,
      failureMessage: message,
    });
    this.props.resetButtons();
  };

  reportSaver = () => {
    const params = {};
    params.user = this.props.user;
    params.report = this.props.payload.report;
    params.rollup = this.props.payload.rollup;
    params.region = this.props.payload.region;
    params.marketplace = this.props.payload.marketplace;
    params.channel = this.props.payload.channel;
    params.timeFrame = this.props.payload.timeFrame;
    params.productLines = this.props.payload.productLines;
    params.component = this.props.payload.component;
    params.currency = this.props.payload.currency;
    params.aggregatedMarketplaces =  this.nonAggregatedPayloads.length ? this.nonAggregatedFlags['aggregatedMarketplaceSelected'] : true;
    params.aggregatedTimeFrames = this.nonAggregatedPayloads.length ? this.nonAggregatedFlags['aggregatedTimeFrameSelected'] : true;
    let columnSets = '';
    for (const set of this.state.selectedColumnSets) {
      if (columnSets) {
        columnSets += ',';
      }
      columnSets += set.id;
    }
    params.columnSets = columnSets;
    let overrides = '';
    for (const override of Object.keys(this.state.columnSetOverrides)) {
      if (overrides) {
        overrides += ',';
      }
      overrides += override;
    }
    params.columnSetOverrides = overrides;
    params['snapshots'] = this.props.snapshots;
    return (
      <ReportSaveWindow
        onCancel={this.cancelSave}
        onSubmit={this.saveReport}
        reportsList={this.props.reportsList}
        params={params}
        formatDate={this.props.formatDate}
        user={this.props.user}
      />
    );
  };

  onContentSelectionChange = (event) => {
    const contentSelectorOptionsGroup = this.state.contentSelectorOptions[0];
    const newVisible = {};
    for (const column of event.detail.contentSelection) {
      newVisible[column] = column;
    }
    let overrides = this.state.payloadsThresholdCrossed ? deepClone(this.state.payloadsThresholdCrossedColumns) : deepClone(this.state.columnSetOverrides);
    let processedOverrides = [];
    for (const column of contentSelectorOptionsGroup.options) {
      // column.visible XOR column.id in newVisible
      if ((!column.visible && column.id in newVisible) || (column.visible && !(column.id in newVisible))) {
        const id = column.id.split(KEY_SEPARATOR)[0];
        if (!processedOverrides.includes(id)) {
          if (id in overrides) {
            delete overrides[id];
          } else {
            overrides[id] = id;
          }
          processedOverrides.push(id);
        }
      }
    }
    if (this.state.payloadsThresholdCrossed) {
      this.setState({ payloadsThresholdCrossedColumns: overrides });
      logger.info('User added columns to Payload Threshold Crossed Report',{report : this.state.payloadsThresholdCrossedColumns});
    }
    this.setState({ columnSetOverrides: overrides });
    const sets = this.state.selectedColumnSets.length ? this.state.selectedColumnSets : [];
    const columnSetArr = sets.map((set) => set['id']);
    let columns = contentSelectorOptionsGroup.options.filter((opt) => this.getColumnVisibility(opt.id, sets));
    const columnArr = [...new Set(columns.map((col) => col['id'].split(KEY_SEPARATOR)[0]))];
    if (!this.state.payloadsThresholdCrossed) {
      this.setPreferences(columnArr, columnSetArr);
    }
    this.setState({
      contentSelectorOptions: [
        {
          label: contentSelectorOptionsGroup.label,
          options: contentSelectorOptionsGroup.options.map((opt) => ({
            id: opt.id,
            label: opt.label,
            editable: opt.editable,
            visible: this.getColumnVisibility(opt.id, sets),
          })),
        },
      ],
    });
  };

  getColumnVisibility = (id, columnSets) => {
    id = id.split(KEY_SEPARATOR)[0];
    for (let columnSet of columnSets) {
      if (columnSet['columns'].includes(id)) {
        return !(id in this.state.columnSetOverrides);
      }
    }
    return id in this.state.columnSetOverrides;
  };

  setColumnSets = (chosen) => {
    const contentSelectorOptionsGroup = this.state.contentSelectorOptions[0];
    let sets = chosen.detail.selectedOptions;
    const overrides = deepClone(this.state.columnSetOverrides);
    for (const column of this.getColumnSetsDiff(this.state.selectedColumnSets, sets)) {
      delete overrides[column];
    }
    this.setState({ selectedColumnSets: [], columnSetOverrides: overrides });
    if (sets === undefined || !sets.length) {
      const defaultSet = this.props.columnsJson.default;
      this.setState({ columnSetOverrides: defaultSet.columns });
      sets = [defaultSet];
      this.setPreferences(defaultSet.columns, []);
    } else {
      this.setState({ selectedColumnSets: sets });
      let columnSetsArr = sets.map((set) => set['value']).reduce((a, b) => a.concat(b), []);
      let columnsArr = [
        ...new Set(
          contentSelectorOptionsGroup.options
            .filter((opt) => this.getColumnVisibility(opt.id, sets))
            .map((opt) => opt.id.split(KEY_SEPARATOR)[0])
        ),
      ];
      if (!this.state.payloadsThresholdCrossed) {
        this.setPreferences(columnsArr, columnSetsArr);
      }
    }
    this.setState({
      contentSelectorOptions: [
        {
          label: contentSelectorOptionsGroup.label,
          options: contentSelectorOptionsGroup.options.map((opt) => ({
            id: opt.id,
            label: opt.label,
            editable: opt.editable,
            visible: this.getColumnVisibility(opt.id, sets),
          })),
        },
      ],
    });
  };

  getColumnSetsDiff = (oldSets, newSets) => {

    const originalColumns = this.flattenColumnSets(oldSets);
    const newColumns = this.flattenColumnSets(newSets);
    for (const column of Object.keys(newColumns)) {
      if (column in originalColumns) {
        delete originalColumns[column];
        delete newColumns[column];
      }
    }
    return Object.keys(originalColumns).concat(Object.keys(newColumns));
  };

  flattenColumnSets = (sets) => {
    const columns = {};
    for (const set of sets) {
      if (set.id === 'All Columns') {
        for (const column of this.state.contentSelectorOptions[0].options) {
          columns[column.id] = column.id;
        }
      } else {
        for (const column of set.columns) {
          columns[column] = column;
        }
      }
    }
    return columns;
  };

  buildRow = (row, dataSetId = null, displayFormatFunctions) => {

    const { columnDefinitions } = this.state;

    const rowObj = {};
    for (const column of columnDefinitions) {
      const columnIdPrefix = dataSetId ? column.id.replace(`${KEY_SEPARATOR}${dataSetId}`, '') : column.id;

      if (row[columnIdPrefix] || row[columnIdPrefix] === 0) {
        const formatFunction = displayFormatFunctions?  displayFormatFunctions[columnIdPrefix] : null;
        rowObj[column.id] = formatFunction ? formatFunction(row[columnIdPrefix]) : row[columnIdPrefix];
      }
    }

    return rowObj;
  };

  onSubComponentExpandOrCollapse = (item) => {
    const parentComponentName = deriveComponentKey(item.component);
    const itemId = item.id;

    if (
      (this.state.currentPayload.parentComponent === parentComponentName &&
        this.state.currentPayload.channel === item.channel) ||
      this.state.expandedMap[itemId]
    ) {
      this.onExpandOrCollapse(itemId);
    } else {
      logger.info('Fetching subcomponent', {component: item.component});
      this.setState({
        subComponentLoading: { ...this.state.subComponentLoading, [item.id]: true },
      });

      const hasNonAggregratedPayloads = this.nonAggregatedPayloads.length > 0;
      const dataSets = hasNonAggregratedPayloads ? this.nonAggregatedPayloads : this.props.snapshots;
      for (let index = 0; index < dataSets.length; index++) {
        const payload = hasNonAggregratedPayloads ? this.nonAggregatedPayloads[index] : this.props.payload;
        const payloadUpdated = { ...payload, channel: item.channel, parentComponent: parentComponentName };

        logger.info('Pulling subComponent with payload: ' + JSON.stringify(payloadUpdated));
        this.pullSubComponentData(payloadUpdated, index, item);
      }
    }
  };

  pullSubComponentData = (payload, index, item) => {
    this.client.pullAccuracyData(
      this.onSubComponentPullSuccess,
      this.onSubComponentFailure,
      payload,
      this.state.queryExecutionId,
      this.props.snapshots,
      index,
      item
    );
  };

  onSubComponentPullSuccess = (response, index, payload, item) => {
    this.subComponentRows[index] = response.accuracyList;
    this.subComponentRowsLoaded = this.subComponentRowsLoaded + 1;
    const noOfRowsToLoad = this.nonAggregatedPayloads.length
      ? this.nonAggregatedPayloads.length
      : this.props.snapshots.length;

    if (this.subComponentRowsLoaded === noOfRowsToLoad) {
      this.setState({
        currentPayload: payload,
      });
      this.updateViewWithLoadedSubComponents(item, this.subComponentRows, payload.parentComponent);
      this.resetLoadedSubComponents(item);
    }
  };

  updateViewWithLoadedSubComponents = (item, subComponentRows, parentComponent) => {
    let accuracyMap = deepClone(this.state.globalAccuracyMap);

    let subComponentNames = new Set();
    let expansionItems = new Set();

    for (let i = 0; i < subComponentRows.length; ++i) {
      const snapshotId = this.props.snapshots.length > 1 ? this.props.snapshots[i].id : null;
      const dataSetId = this.nonAggregatedPayloads.length ? this.nonAggregatedHeadings[i] : snapshotId;
      this.processSubComponentRows(subComponentRows[i], dataSetId, accuracyMap, parentComponent);

      subComponentRows[i]
        .map((r) => extractSubComponentName(r.component))
        .forEach((n) => subComponentNames.add(n));

      expansionItems.add(item.id);
    }

    const { expandedMap } = this.state;
    for (let id of expansionItems) {
      expandedMap[id] = !expandedMap[id];
    }
    this.setState({ expandedMap: expandedMap });

    this.setState({ globalAccuracyMap: accuracyMap });
    this.updateHierarchy(this.state.root, accuracyMap, subComponentNames);

    const newRows = [];
    this.flattenHierarchy(this.state.root, newRows);

    this.setState({ rows: newRows });
  };

  resetLoadedSubComponents = (item) => {
    this.subComponentRows = [];
    this.subComponentRowsLoaded = 0;
    this.setState({ subComponentLoading: { ...this.state.subComponentLoading, [item.id]: false } });
  };

  onSubComponentFailure = (error) => {
    this.setState({ subComponentLoading: {} });
    console.log(error);
  };

  processSubComponentRows = (subComponentRows, dataSetId, accuracyMap, parentComponent) => {
    console.log('Process sub-component rows:' + (subComponentRows || []).map((r) => r.component));
    subComponentRows
      .filter((subComponentRow) => subComponentRow.component !== 'Units')
      .forEach((row) => {
        const key = row.channel + extractSubComponentName(row.component);
        const rowObj = { ...this.buildRow(row, dataSetId), parentComponent };
        accuracyMap[key] = accuracyMap[key] ? { ...accuracyMap[key], ...rowObj } : rowObj;
      });
  };

  getLevelForComponentRows = () => {
    return this.props.payload.channel.toUpperCase() === CHANNELS.ALL ? 1 : 0;
  }

  generateColumns = () => {
    let columns = [];
    let columnOptions = [];
    const wrapLineColumns = ['productGroup', 'productLineName', 'parentProductLineName'];
    const levelForComponentRows = this.getLevelForComponentRows();

    for (let [key, heading] of this.zip(this.keys, this.headings)) {
      let columnEntry = {};
      columnEntry['id'] = key;
      columnEntry['header'] = () => this.formatHeadingDisplay(heading, this.getFormat(key));
      columnEntry['cell'] = (item) => this.formatCellDisplay(item[key], this.getFormat(key), item);
      columnEntry['minWidth'] = wrapLineColumns.includes(key) ? '200px' : '100px';
      columnEntry['allowLineWrap'] = wrapLineColumns.includes(key);
      if (key === 'component') {
        columnEntry.cell = (item) => {
          let cellElement;
          const name = item[key];
          const treeMode = !this.state.expandedMap[item.id] ? TreeLineModes.None : TreeLineModes.Bottom;
          if (
            this.props.payload.report.toUpperCase() === REPORT_TYPE.COMPONENT &&
            item.children !== null &&
            item.children.length > 0 &&
            item.parent !== null &&
            item.level > levelForComponentRows
          ) {
            cellElement = (
              <div className="ui-util-flex-row ui-util-flex-align-center">
                {item.level > 0 && (
                  <TreeLine
                    mode={
                      item.isFirst ? TreeLineModes.UpsideDownL : item.isLast ? TreeLineModes.L : TreeLineModes.Default
                    }
                    level={item.level}
                  />
                )}
                {this.props.featureList && this.props.featureList.includes('subcomponent_' + item.component) && (
                  <div>
                    <ButtonWithTreeLines
                      iconType={this.state.expandedMap[item.id] ? COLLAPSE : EXPAND}
                      onClick={this.onSubComponentExpandOrCollapse.bind(this, item)}
                      mode={treeMode}
                      expand={item.style}
                      level={item.level}
                      isLoading={this.state.subComponentLoading[item.id]}
                    />
                  </div>
                )}
                <ComponentLink componentName={name} mapping={COMPONENT_TO_LINK} level={item.level} />
              </div>
            );
          } else if (
            this.props.payload.report.toUpperCase() === REPORT_TYPE.COMPONENT &&
            item.children !== null &&
            item.children.length > 0
          ) {
            cellElement = (
              <div className="ui-util-flex-row ui-util-flex-align-center">
                {item.level > 0 && (
                  <TreeLine
                    mode={
                      item.isFirst ? TreeLineModes.UpsideDownL : item.isLast ? TreeLineModes.L : TreeLineModes.Default
                    }
                    level={item.level}
                  />
                )}
                <ButtonWithTreeLines
                  iconType={this.state.expandedMap[item.id] ? COLLAPSE : EXPAND}
                  onClick={this.onExpandOrCollapse.bind(this, item.id)}
                  mode={treeMode}
                  expand={item.style}
                  level={item.level}
                />
                <ComponentLink componentName={name} mapping={COMPONENT_TO_LINK} level={item.level} />
              </div>
            );
          } else {
            cellElement = (
              <div className="ui-util-flex-row ui-util-flex-align-center" style={this.getCellDisplayStyle(item)}>
                {this.props.payload.report.toUpperCase() === REPORT_TYPE.COMPONENT &&
                  item.parent !== null &&
                  !this.state.expandedMap[item.id] &&
                  item.level > 0 && (
                    <TreeLine
                      mode={
                        item.isFirst ? TreeLineModes.UpsideDownL : item.isLast ? TreeLineModes.L : TreeLineModes.Default
                      }
                      level={item.level}
                    />
                  )}
                <ComponentLink componentName={name} mapping={COMPONENT_TO_LINK} level={item.level} />
              </div>
            );
          }
          let whichBorder = dataStarAttributeBorderNormal;
          if (item.children && item.children.length > 0) {
            whichBorder = dataStarAttributeTopBorderDark;
          } else if (item.isLast) {
            whichBorder = dataStarAttributeBottomBorderDark;
          }
          return React.cloneElement(cellElement, {
            [dataStarAttributeBorder]: whichBorder,
          });
        };
      }
      columns.push(columnEntry);

      const option = {};
      option.id = key;
      option.label = heading;
      option.editable = true;

      // If we are loading a report via URL, use defined columnSets for this query only
      const sets = this.props.columnsJson.componentSets;
      if (
        !this.state.initialLoadDone &&
        (this.props.columnSetsToLoad.length || this.props.columnSetOverridesToLoad.length)
      ) {

        const columnSets = [];
        const overrides = {};

        if (this.props.columnSetsToLoad.length) {
          for (const set of sets) {
            if (this.props.columnSetsToLoad.includes(set.id)) {
              columnSets.push(set);
            }
          }
          this.setState({ selectedColumnSets: columnSets });
        }
        if (this.props.columnSetOverridesToLoad.length) {
          for (const override of this.props.columnSetOverridesToLoad) {
            overrides[override] = override;
          }
          this.setState({ columnSetOverrides: overrides });
        }
        option.visible = this.getColumnVisibility(key, columnSets);
      } else {

        let column = key.split(KEY_SEPARATOR)[0];
        if (this.props.payload.report.toUpperCase() === REPORT_TYPE.COMPONENT) {
          if (this.state.payloadsThresholdCrossed) {
            option.visible = this.props.columnsJson.columnsForPayloadsThresholdCrossed.columns.includes(column);
            const columnSets = sets.filter(() => this.props.columnsJson.columnsForPayloadsThresholdCrossed.columns.includes(column));
            this.setState({ selectedColumnSets: columnSets });
          } else {
            option.visible =
              this.reportPreferences['componentReport']['columns'].length &&
              this.reportPreferences['componentReport']['columns'].includes(column);
            const columnSets = this.reportPreferences['componentReport']['columnSets'].length
              ? sets.filter((set) => this.reportPreferences['componentReport']['columnSets'].includes(set['id']))
              : [];
            this.setState({ selectedColumnSets: columnSets });
          }
        } else if (this.props.payload.report.toUpperCase() === REPORT_TYPE.PL) {
          option.visible =
            this.reportPreferences['plReport']['columns'].length &&
            this.reportPreferences['plReport']['columns'].includes(column);
          this.setState({ selectedColumnSets: this.props.columnsJson.plSets });
        } else {
          option.visible = true;
        }
      }
      columnOptions.push(option);
    }
    return {
      columnDefinitions: columns,
      contentSelectorOptions: [
        {
          options: columnOptions,
        },
      ],
    };
  };

  updateBorders = () => {
    const nodes = Array.from(document.querySelectorAll(`[${dataStarAttributeBorder}]`));
    for (let i = 0; i < nodes.length; i += 1) {
      try {
        Array.from(nodes[i].parentElement.children).forEach((element) => {
          const borderType = nodes[i].getAttribute(dataStarAttributeBorder);
          if (borderType !== dataStarAttributeBorderNormal) {
            if (borderType === dataStarAttributeTopBorderDark) {
              element.classList.add('dark-top-border');
              element.classList.remove('dark-bottom-border');
            } else if (borderType === dataStarAttributeBottomBorderDark) {
              element.classList.add('dark-bottom-border');
              element.classList.remove('dark-top-border');
            }
          } else {
            element.classList.remove('dark-bottom-border');
            element.classList.remove('dark-top-border');
          }
        });
      } catch (e) {
        console.log(`Error${e}`);
      }
    }
  };

  formatHeadingDisplay = (heading, format) => {
    if (heading.startsWith('[')) {
      let separator = heading.indexOf(']') + 1;
      heading = (
        <span>
          {heading.slice(0, separator)}
          <br />
          {heading.slice(separator)}
        </span>
      );
    }

    return format !== 'string' ? (
      <span className="awsui-util-t-r awsui-util-d-b">{heading}</span>
    ) : (
      <span className="awsui-util-d-b">{heading}</span>
    );
  };

  formatCellDisplay = (cellValue, format, item = null) => {
    const displayInMillions = format === 'monetary' && this.state.displayInMillions;
    if (displayInMillions && cellValue) {
      cellValue /= 1000000;
      cellValue = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(cellValue);
    } else if ((cellValue === 0.0 || cellValue === -0.0) && format === 'decimal') {
      cellValue = 'N/A';
    } else if (cellValue && format === 'decimal') {
      cellValue = isNaN(cellValue) ? 'N/A' : cellValue.toFixed(2);
    } else if ((cellValue === 0.0 || cellValue === -0.0) && format === 'percent') {
      cellValue = '0.00  %';
    } else if (cellValue && format === 'percent') {
      cellValue = isNaN(cellValue) ? 'N/A' : cellValue.toFixed(2) + ' %';
    } else if (this.state.displayInMillions === false && cellValue) {
      cellValue = isNaN(cellValue)
        ? cellValue
        : new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(cellValue);
    }

    const cellStyle = this.getCellDisplayStyle(item);

    return format !== 'string' ? (
      <span className="awsui-util-t-r awsui-util-d-b" style={cellStyle}>
        {cellValue}
      </span>
    ) : (
      <span className="awsui-util-d-b">{cellValue}</span>
    );
  };

  getCellDisplayStyle = (item) => {
    return item && item['level'] === this.getLevelForComponentRows() + 2 ? { color: '#006600', fontStyle: 'italic' } : null;
  };

  onToggleMillions = (chosen) => {
    this.setState({ displayInMillions: chosen.detail.checked, tableKey: this.state.tableKey + 1 });
  };

  removeMissingData = () => {
    // Handle missing data for certain marketplaces. We are not removing the payload from nonAggregatedPayloads but just removing it from headings.
    if ( Object.keys(this.missingDataIndex).length !== 0 ) {
      for ( let missingIndex of Object.keys(this.missingDataIndex) ) {
        if ( this.nonAggregatedHeadings.includes(this.missingDataIndex[missingIndex]) ) {
          this.nonAggregatedHeadings.splice( this.nonAggregatedHeadings.indexOf(this.missingDataIndex[missingIndex]), 1 );
          this.summaryMaps.splice( this.nonAggregatedHeadings.indexOf(this.missingDataIndex[missingIndex]), 1 );
        }
      }
    }
  };

  buildSummaryTables = () => {
    let nameRows = [
      'First Answer Accuracy',
      'First Answer Accuracy (Abs)',
      'Last Answer Accuracy',
      'Last Answer Accuracy (Abs)',
    ];
    let nameCols = ['Total Accuracy', 'Transactional Accuracy', 'Standard Rate Accuracy', 'Predictive Accuracy'];
    let colKeys = ['cpAccuracy', 'transactionAccuracy', 'standardRateAccuracy', 'predictiveAccuracy'];

    let sumRows = [];
    if (this.nonAggregatedPayloads.length) {
      this.removeMissingData();
      for (let row = 0; row < nameRows.length; row++) {
        let sumRow = { accuracyName: this.formatCellDisplay(nameRows[row], 'string') };
        for (let key of colKeys) {
          for (let table = 0; table < this.nonAggregatedHeadings.length; table++) {
            if (this.summaryMaps[table]) {
              let val = Object.values(this.summaryMaps[table])[row];
              let newKeySlice = key;
              newKeySlice = newKeySlice + KEY_SEPARATOR + this.nonAggregatedHeadings[table];
              sumRow[newKeySlice] = this.formatCellDisplay(val[key], 'percent');
            } else {
              console.log('No summary data found.');
            }
          }
        }
        sumRows.push(sumRow);
      }
    } else {
      for (let i = 0; i < nameRows.length; i++) {
        let sumRow = { accuracyName: this.formatCellDisplay(nameRows[i], 'string') };
        for (let key of colKeys) {
          for (let table = 0; table < this.props.snapshots.length; table++) {
            if (this.summaryMaps[table] == null) {
              console.log('Summary Table not found for snapshot: ' + this.props.snapshots[table].id);
            } else {
              let val = Object.values(this.summaryMaps[table])[i];
              let newKey = key;
              if (this.props.snapshots.length > 1) {
                newKey = newKey + KEY_SEPARATOR + this.props.snapshots[table].name;
              }
              sumRow[newKey] = this.formatCellDisplay(val[key], 'percent');
            }
          }
        }
        sumRows.push(sumRow);
      }
    }

    let sumCols = [
      {
        id: '',
        header: this.formatHeadingDisplay('', 'string'),
        cell: (item) => item['accuracyName'],
      },
    ];
    if (this.nonAggregatedPayloads.length) {
      for (let k = 0; k < nameCols.length; k++) {
        for (let j = 0; j < this.nonAggregatedHeadings.length; j++) {
          let keySlice = colKeys[k],
            name = nameCols[k];
          name = '[' + this.nonAggregatedHeadings[j] + ']' + name;
          keySlice = keySlice + KEY_SEPARATOR + this.nonAggregatedHeadings[j];
          let sumCol = {
            id: name,
            header: this.formatHeadingDisplay(name, 'percent'),
            cell: (item) => item[keySlice],
          };
          sumCols.push(sumCol);
        }
      }
    } else {
      for (let i = 0; i < nameCols.length; i++) {
        for (let j = 0; j < this.summaryMaps.length; j++) {
          let key = colKeys[i],
            name = nameCols[i];
          if (this.props.snapshots.length > 1 && name) {
            name =
              (this.props.snapshots[j].id === this.state.liveSnapshotId
                ? '[live] '
                : '[' + this.props.snapshots[j].name + '] ') + name;
            key = key + KEY_SEPARATOR + this.props.snapshots[j].name;
          }
          let sumCol = {
            id: name,
            header: this.formatHeadingDisplay(name, 'percent'),
            cell: (item) => item[key],
          };
          sumCols.push(sumCol);
        }
      }
    }

    this.setState({ summaryTableRows: sumRows });
    this.setState({ summaryTableColumnDefinitions: sumCols });
  };

  getMissingDataMessage = () => {
    let messages = [];
    if (Object.keys(this.missingDataIndex).length === 0) return '';
    for (const [index, missingData] of Object.entries(this.missingDataIndex)) {
      messages.push(<div key={index}> {'The marketplace ' + missingData + ' has no data for the selected TimeFrame.' } </div>);
    }
    return (
      <div className="awsui-util-t-c">
        <div className="awsui-util-pt-xs">
          <div className="awsui-util-pt-xs awsui-util-mb-xs">
            <Alert
              onDismiss={this.disableMissingDataMessage}
              visible={this.state.missingDataMessageVisible}
              dismissAriaLabel="Close alert"
              dismissible>
              <div> {messages} </div>
            </Alert>
          </div>
        </div>
      </div>
    )
  };

  disableMissingDataMessage = () => {
    this.setState({ missingDataMessageVisible: false })
  };

  getPayloadsThresholdCrossedMessage = () => {
    return (
      <div className="awsui-util-t-c">
        <div className="awsui-util-pt-xs">
          <div className="awsui-util-pt-xs awsui-util-mb-xs">
            <Alert
              header="The UI might get slow because of the number of comparisons."
              onDismiss={this.disablePayloadsThresholdCrossedMessage}
              visible={this.state.payloadsThresholdCrossedMessageVisible}
              dismissAriaLabel="Close alert"
              dismissible>
              <div> We have just loaded First Answer, Accuracy Benchmark and FA: Per Unit Variance (Net) columns by default.
                <br />
                    You can <b> download </b> the data to see all the columns in an excel file
                    <b> OR </b> You can add columns by clicking on the <b> gear icon </b>.
              </div>
            </Alert>
          </div>
        </div>
      </div>
    )
  };

  disablePayloadsThresholdCrossedMessage = () => {
    this.setState({ payloadsThresholdCrossedMessageVisible: false })
  };

  isAccuracyDriversEnabled = () => {
    return this.state.loadDone && !this.state.loadError && this.props.payload.report.toUpperCase() === REPORT_TYPE.COMPONENT
      && !this.accuracyDriversDisabled && this.props.featureList && this.props.featureList.includes('accuracy-drivers');
  };

  isAccuracySummaryEnabled = () => {
    return this.state.loadDone && !this.state.loadError && this.props.payload.report.toUpperCase() === REPORT_TYPE.COMPONENT;
  };

  isMissingData = () => {
    return this.state.loadDone && this.nonAggregatedPayloads.length && Object.keys(this.missingDataIndex).length !== 0;
  };

  isPayloadsThresholdCrossed = () => {
    return this.state.payloadsThresholdCrossed && this.state.loadDone && this.props.payload.report.toUpperCase() === REPORT_TYPE.COMPONENT;
  };


  render() {
    const PAGE_SELECTOR_OPTIONS = [
      { value: 10, label: '10' },
      { value: 25, label: '25' },
      { value: 50, label: '50' },
      { value: 100, label: '100' },
      { value: 500, label: '500' },
    ];
    const loadingText = `Data loaded: ${this.state.queryDataScanned}`;

    return (
      <div>
        {this.state.saveInProgress && this.reportSaver()}
        {this.state.downloadInProgress && this.onDownloadData()}
        {this.isMissingData() && this.getMissingDataMessage()}
        {this.isAccuracySummaryEnabled() && (
          <ExpandableSection header={"Accuracy Summary"} expanded>
          <Table
            className="accuracy-table-container"
            columnDefinitions={this.state.summaryTableColumnDefinitions}
            items={this.state.summaryTableRows}
            loading={!this.state.loadDone}
            stickyHeader={true}
            wrapLines={false}
            empty={
              <div className="awsui-util-t-c">
                <div className="awsui-util-pt-xs awsui-util-mb-xs">
                  <b>This table was empty or could not be found.</b>
                </div>
              </div>
            }
          />
          </ExpandableSection>)}
        {this.isPayloadsThresholdCrossed() && this.getPayloadsThresholdCrossedMessage()}
        {this.isAccuracyDriversEnabled() && <AccuracyDriversTable payload={this.accuracyDriversPayload}
                                                                  marketplaceIdToLabel={this.state.marketplaceIdToLabel}
                                                                  featureList={this.props.featureList}/>}
        {this.state.loadError && (
          <FlashBar
              items={[
                  {
                      type: 'error',
                      dismissible: true,
                      content: (
                          <div>
                              {this.props.payload.report.toUpperCase() === REPORT_TYPE.PL && this.errorMessage}
                              {this.state.errorState} fetching the data, retry the request. Contact us if the error persists.
                              PI Tools dev team has been notified via ticket:{' '}
                              <a href={'https://tiny.amazon.com/11ia80cg5'} target="_blank">
                                  Ticket Queue
                              </a>
                          </div>
                      )
                  }
                  ]}/>
        )}
        <br />
        {this.state.loadDone && this.props.payload.report.toUpperCase() === REPORT_TYPE.COMPONENT && (
          <BaselineColumnButton
            text="Benchmark Selection"
            sets={this.props.columnsJson.componentSets}
            selected={this.state.selectedColumnSets}
            onChange={this.setColumnSets}
          />
        )}
        <br />
        <Table
          className="ZebraStripesTable accuracy-table-container"
          columnDefinitions={this.state.tableKey && this.state.columnDefinitions}
          loading={!this.state.loadDone}
          loadingText={this.state.queryDataScanned == null ? 'Loading table...' : loadingText}
          items={this.state.rows}
          onVisibleItemsChange={() => this.updateBorders()}
          onContentSelectionChange={this.onContentSelectionChange}
          stickyHeader={true}
          onBeforeUnstick={(event) => event.preventDefault()}
          wrapLines={this.props.payload.report.toUpperCase() === REPORT_TYPE.PL}
          data-cy="accuracy-table"
          header={
            this.props.payload.report.toUpperCase() === REPORT_TYPE.PL && (
              <div className="awsui-util-action-stripe">
                <div className="awsui-util-action-stripe-title" />
                <div className="awsui-util-action-stripe-group">
                  <Toggle
                    checked={this.state.displayInMillions}
                    disabled={!this.state.loadDone}
                    onChange={this.onToggleMillions}
                  >
                    Display in Millions
                  </Toggle>
                </div>
              </div>
            )
          }
          empty={
            <div className="awsui-util-t-c">
              <div className="awsui-util-pt-xs awsui-util-mb-xs">
                <b>This table was empty or could not be found.</b>
              </div>
            </div>
          }
        >
          {this.props.payload.report.toUpperCase() === REPORT_TYPE.COMPONENT && (
            <Toggle
              checked={this.state.displayInMillions}
              disabled={!this.state.loadDone}
              onChange={this.onToggleMillions}
            >
              Display in Millions
            </Toggle>
          )}
          {this.props.payload.report.toUpperCase() === REPORT_TYPE.PL && (
            <TableFiltering filteringPlaceholder="Filter" />
          )}
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
          <TablePagination pageSize={250} />
          <TablePreferences id="table-preferences" title="Preferences" confirmLabel="Confirm" cancelLabel="Cancel">
            <TablePageSizeSelector title="Page size" options={PAGE_SELECTOR_OPTIONS} value={250} />
            <TableContentSelector title="Select visible columns" options={this.state.contentSelectorOptions} />
          </TablePreferences>
        </Table>

      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/content/accuracy_table.jsx